import { ArrowDown } from "lucide-react";
import * as React from "react";
import { graphql, useFragment } from "react-relay";
import { useLocation } from "react-router";
import { AgentPendingQuestionType } from "src/components/ui/chat/AgentQuestion";
import { AgentThoughtProcess } from "src/components/ui/chat/AgentThoughtProcess";
import { ChatBubble } from "src/components/ui/chat/chat-bubble";
import { ChatMessageList } from "src/components/ui/chat/chat-message-list";
import { LlmChatMessageActions } from "src/components/ui/chat/LlmChatMessageActions";
import { MessageWithToolReferences } from "src/components/ui/chat/message-with-tool-references";
import { MessageAttachmentList } from "src/components/ui/chat/MessageAttachmentList";
import { ChecklistToolCallResult } from "src/components/ui/chat/tool-calls/ChecklistToolCallResult";
import { ToolCallsSection } from "src/components/ui/chat/tool-calls/ToolCallsSection";
import { formatChatTimestamp } from "src/components/ui/chat/utils";
import { Markdown } from "src/components/ui/Markdown";
import { useToast } from "src/hooks/use-toast";
import { useAnswerAgentQuestionMutation } from "src/pages/AskPage/hooks/useAnswerAgentQuestionMutation";

import {
  type ConversationUIMessageMetadata,
} from "../utils/chatMessages";
import type {
  LlmConversationMessageList_llmConversation$data,
  LlmConversationMessageList_llmConversation$key,
} from "./__generated__/LlmConversationMessageList_llmConversation.graphql";
import AgentLoadingBubble from "./AgentLoadingBubble";
import TierZeroAvatar from "./TierZeroAvatar";

type LlmConversationMessage = LlmConversationMessageList_llmConversation$data["messages"][number];

interface LlmConversationMessageListProps {
  llmConversation: LlmConversationMessageList_llmConversation$key;
  userAvatar?: string;
  userInitials: string;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  isPending?: boolean;
  hideUserMessages?: boolean;
  hideAvatars?: boolean;
  /** Skip the first user message (e.g. to hide internal system instructions) */
  skipFirstUserMessage?: boolean;
  activeInteractionId?: string;
  activeStatusMessage?: string | null;
  isOnboarding?: boolean;
  /** Override URL hash for permalink scrolling (use when component is inside MemoryRouter) */
  urlHash?: string;
  /** Content to render at the top of the message list, inside the scroll container */
  headerContent?: React.ReactNode;
  /** Force all sources sections to be collapsed by default */
  collapseSources?: boolean;
}

const llmConversationFragment = graphql`
  fragment LlmConversationMessageList_llmConversation on GraphQLLlmConversation {
    id
    pendingToolUses {
      id
      toolUseId
      status
      reviewedBy
      reviewedAt
    }
    llmInteractions(last: 10) {
      id
      pendingQuestions {
        id
        toolUseId
        questionsJson
        answersJson
        status
      }
    }
    messages {
      id
      createdAt
      updatedAt
      messageRole
      messageType
      content
      output
      llmInteractionId
      attachments {
        originalName
        mimetype
        size
        downloadToken
      }
      messageMetadata {
        agentVersion
      }
      toolCalls {
        ...ToolCallsSection_toolCalls
        ...ChecklistToolCallResult_toolCall
      }
      agentThoughtProcess {
        text
        thinking
        thinkingSummary
        subagentDescription
        subagentOutput
        subagentItems {
          text
          thinking
          thinkingSummary
          toolUse {
            ...ToolCallsSection_toolCalls
          }
        }
        toolUse {
          ...ToolCallsSection_toolCalls
        }
      }
      ...messageWithToolReferences_formattedMessage
      ...AgentThoughtProcess_formattedMessage
    }
    ...AgentThoughtProcess_llmConversation
  }
`;

const LlmConversationMessageList: React.FC<LlmConversationMessageListProps> = ({
  llmConversation,
  userAvatar,
  userInitials,
  messagesEndRef,
  isPending,
  hideUserMessages = false,
  hideAvatars = false,
  skipFirstUserMessage = false,
  activeStatusMessage,
  isOnboarding = false,
  urlHash,
  headerContent,
  collapseSources = false,
}) => {
  const data = useFragment(llmConversationFragment, llmConversation);
  const llmConversationId = data.id;
  const { answerQuestion, isInFlight: isSubmittingQuestion } = useAnswerAgentQuestionMutation();
  const { toast } = useToast();

  // Build a map of interaction ID to pending questions (including timed out and answered ones for display)
  const pendingQuestionsByInteraction = React.useMemo(() => {
    const map = new Map<string, AgentPendingQuestionType[]>();
    for (const interaction of data.llmInteractions || []) {
      const questions = (interaction.pendingQuestions || [])
        .filter((q: any) => q.status === "PENDING" || q.status === "TIMEOUT" || q.status === "ANSWERED")
        .map((q: any) => ({
          toolUseId: q.toolUseId,
          questionsJson: q.questionsJson,
          answersJson: q.answersJson,
          status: q.status as "PENDING" | "TIMEOUT" | "ANSWERED",
        }));
      if (questions.length > 0) {
        map.set(interaction.id, questions);
      }
    }
    return map;
  }, [data.llmInteractions]);

  const handleQuestionSubmit = React.useCallback(
    async (toolUseId: string, interactionId: string, answers: any[]) => {
      try {
        const success = await answerQuestion(
          toolUseId,
          answers,
          llmConversationId,
          interactionId
        );
        if (!success) {
          toast({
            title: "Question Expired",
            description: "This question has timed out. Please re-ask your question to continue.",
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Failed to submit answer:", error);
        toast({
          title: "Question Expired",
          description: "This question has timed out. Please re-ask your question to continue.",
          variant: "error",
        });
      }
    },
    [answerQuestion, llmConversationId, toast]
  );

  // Convert messages from fragment to UI messages
  const serverUiMessages = React.useMemo(() => {
    let messages = data.messages || [];
    if (hideUserMessages) {
      messages = messages.filter(msg => msg.messageRole === "AGENT");
    } else if (skipFirstUserMessage) {
      let skipped = false;
      messages = messages.filter(msg => {
        if (!skipped && msg.messageRole === "HUMAN") {
          skipped = true;
          return false;
        }
        return true;
      });
    }

    return messages.map((msg) => ({
      id: msg.id,
      role: msg.messageRole === "AGENT" ? ("assistant" as const) : ("user" as const),
      metadata: {
        messageId: msg.id,
        createdAt: msg.createdAt,
        updatedAt: msg.updatedAt,
        messageRole: msg.messageRole,
        messageType: msg.messageType,
        // messageMetadata is accessed through the fragment in MessageWithToolReferences
        graphqlMessage: msg,
      } as any, // Cast to any to satisfy type checker - metadata structure is flexible
      parts: msg.output ? [{ type: "text" as const, text: msg.output }] : [],
    }));
  }, [data.messages, hideUserMessages, skipFirstUserMessage]);

  // Just use server messages directly
  const displayMessages = serverUiMessages;

  const messageListRef = React.useRef<HTMLDivElement | null>(null);
  const endOfListRef = React.useRef<HTMLDivElement | null>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(true);
  // In onboarding mode, start with wasAtBottom=false to prevent initial auto-scroll
  const wasAtBottomRef = React.useRef(!isOnboarding);

  // Keep scroll behavior in sync with onboarding mode changes
  React.useEffect(() => {
    wasAtBottomRef.current = !isOnboarding;
  }, [isOnboarding]);

  const isAtBottom = React.useCallback((element: HTMLElement) => {
    const threshold = 8;
    return element.scrollHeight - element.scrollTop - element.clientHeight <= threshold;
  }, []);

  // Track scroll position to know if user was at bottom before content changes
  React.useEffect(() => {
    const list = messageListRef.current;
    if (!list) return;

    const handleScroll = () => {
      wasAtBottomRef.current = isAtBottom(list);
    };

    list.addEventListener("scroll", handleScroll);
    return () => list.removeEventListener("scroll", handleScroll);
  }, [isAtBottom]);

  const scrollToLastMessage = React.useCallback((options?: { smooth?: boolean }) => {
    const list = messageListRef.current;
    if (!list) return;

    const behavior: ScrollBehavior = options?.smooth ? "smooth" : "auto";

    // Find the last message element
    const messageElements = list.querySelectorAll("[data-message-id]");
    const lastMessage = messageElements[messageElements.length - 1];

    if (lastMessage) {
      lastMessage.scrollIntoView({ behavior, block: "start" });
    } else {
      // Fallback to scrolling to the end marker if no messages found
      if (messagesEndRef?.current) {
        messagesEndRef.current.scrollIntoView({ behavior });
      }
    }
  }, [messagesEndRef]);

  // Auto-scroll when messages change or are updated (including during agent thought process)
  React.useEffect(() => {
    const list = messageListRef.current;
    if (!list || displayMessages.length === 0) return;

    // Skip auto-scroll on initial load when in onboarding mode
    // This ensures the user can see the onboarding banner at the top
    if (isOnboarding && !wasAtBottomRef.current) return;

    // Only auto-scroll if user was at the bottom before content changed
    // This prevents auto-scroll if user has scrolled up to read previous messages
    if (!wasAtBottomRef.current) return;

    // For dynamic content like thought process accordions, use multiple scroll attempts
    const scrollAttempts = isPending ? 3 : 1;
    const scrollWithRetry = (attempt: number) => {
      setTimeout(() => {
        // If user scrolled away during the retry chain, stop scrolling
        if (!wasAtBottomRef.current) return;

        // During thinking/pending, scroll to absolute bottom to keep up with expanding content
        // After thinking is done, scroll to top of last message for better reading experience
        if (isPending && messagesEndRef?.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        } else {
          scrollToLastMessage({ smooth: true });
        }
        if (attempt < scrollAttempts - 1) {
          scrollWithRetry(attempt + 1);
        }
      }, 100 + (attempt * 150));
    };

    scrollWithRetry(0);
  }, [data.messages, displayMessages.length, messagesEndRef, scrollToLastMessage, isPending, isOnboarding]);

  // Scroll to last message when conversation changes (navigating from sidebar)
  // Skip auto-scroll when in onboarding mode so user can see the banner
  React.useEffect(() => {
    if (isOnboarding) return;
    // Small delay to ensure content is rendered
    setTimeout(() => {
      scrollToLastMessage({ smooth: true });
      wasAtBottomRef.current = true;
    }, 200);
  }, [llmConversationId, scrollToLastMessage, isOnboarding]);

  const assignEndRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      endOfListRef.current = node;
      if (messagesEndRef && "current" in messagesEndRef) {
        (messagesEndRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [messagesEndRef]
  );

  const evaluateScrollPosition = React.useCallback(() => {
    const list = messageListRef.current;
    if (!list) return;
    const atBottom = isAtBottom(list);
    setIsScrolledToBottom(atBottom);
  }, [isAtBottom]);

  const handleScroll = React.useCallback(() => {
    evaluateScrollPosition();
  }, [evaluateScrollPosition]);

  const messageLookup = React.useMemo(() => {
    const lookup = new Map<string, LlmConversationMessage>();
    for (const msg of data.messages ?? []) {
      if (msg?.id) {
        lookup.set(msg.id, msg);
      }
    }
    return lookup;
  }, [data.messages]);

  const recentAgentIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (let idx = displayMessages.length - 1; idx >= 0; idx -= 1) {
      const metadata = displayMessages[idx]?.metadata as ConversationUIMessageMetadata | undefined;
      if (metadata?.messageRole === "AGENT") {
        ids.add(displayMessages[idx]!.id);
        if (ids.size >= 2) break;
      }
    }
    return ids;
  }, [displayMessages]);

  // Handle scroll to specific message from URL hash
  const location = useLocation();
  const previousHashRef = React.useRef<string>("");
  // Use urlHash prop if provided (for MemoryRouter contexts), otherwise use location.hash
  const effectiveHash = urlHash ?? location.hash;

  React.useEffect(() => {
    if (!effectiveHash) return;

    // Skip if hash hasn't changed
    if (effectiveHash === previousHashRef.current) return;
    previousHashRef.current = effectiveHash;

    // Parse hash: #message-{id} or #interaction-{id}
    const match = effectiveHash.match(/^#(message|interaction)-(.+)$/);
    if (!match) return;

    const [, type, targetId] = match;

    // Wait for messages to be rendered
    setTimeout(() => {
      // Remove previous highlights
      document.querySelectorAll("[data-message-id], [data-interaction-id]").forEach((element) => {
        element.classList.remove(
          "ring-2",
          "ring-blue-400",
          "bg-primary/10",
          "rounded-lg",
          "shadow-lg",
          "shadow-blue-500/20"
        );
      });

      let targetElements: HTMLElement[] = [];

      if (type === "message") {
        const messageElement = document.querySelector(`[data-message-id="${targetId}"]`);
        if (messageElement) {
          targetElements = [messageElement as HTMLElement];
        }
      } else if (type === "interaction") {
        // Find all messages belonging to this interaction
        const interactionElements = document.querySelectorAll(`[data-interaction-id="${targetId}"]`);
        targetElements = Array.from(interactionElements) as HTMLElement[];
      }

      if (targetElements.length > 0) {
        // Scroll to the first element (typically the user input)
        targetElements[0].scrollIntoView({ behavior: "smooth", block: "center" });

        // Add permanent highlight effect to all elements in the interaction
        targetElements.forEach((element) => {
          element.classList.add(
            "ring-2",
            "ring-blue-400",
            "bg-primary/10",
            "rounded-lg",
            "shadow-lg",
            "shadow-blue-500/20"
          );
        });
      }
    }, 300);
  }, [effectiveHash, displayMessages.length, llmConversationId]);

  // Group messages by interaction for rendering
  const messageGroups = React.useMemo(() => {
    const groups: Array<{ interactionId: string | null; messages: any[] }> = [];
    let currentGroup: { interactionId: string | null; messages: any[] } | null = null;

    displayMessages.forEach((message) => {
      const metadata = message.metadata as ConversationUIMessageMetadata | undefined;
      if (!metadata) return;

      const messageFromFragment = messageLookup.get(message.id) as any;
      const interactionId = messageFromFragment?.llmInteractionId || null;

      // Start a new group if interaction ID changes or no current group
      if (!currentGroup || currentGroup.interactionId !== interactionId) {
        currentGroup = { interactionId, messages: [message] };
        groups.push(currentGroup);
      } else {
        // Add to current group
        currentGroup.messages.push(message);
      }
    });

    return groups;
  }, [displayMessages, messageLookup]);

  return (
    <div className="relative flex h-full">
      <ChatMessageList ref={messageListRef} onScroll={handleScroll} className="flex-1">
        {headerContent}
        <div className="mx-auto flex w-full max-w-7xl flex-col px-2 md:px-4 pt-6 space-y-6">
          {messageGroups.map((group, groupIndex) => {
            const groupKey = group.interactionId || `no-interaction-${groupIndex}`;

            return (
              <div
                key={groupKey}
                {...(group.interactionId ? { "data-interaction-id": group.interactionId } : {})}
                className="transition-all duration-300 space-y-6"
              >
                {group.messages.map((aiMessage) => {
            const metadata = aiMessage.metadata as ConversationUIMessageMetadata | undefined;
            if (!metadata) return null;

            const messageFromFragment = messageLookup.get(aiMessage.id) as any; // Type will be correct after yarn relay
            // Use output field for AGENT_RESPONSE messages
            const messageContent = messageFromFragment?.output;
            const hasMessageMetadata = Boolean(messageFromFragment?.messageMetadata);
            const isAgentMessage = metadata.messageRole === "AGENT";

            // Intermediary messages are AGENT_PROCESS type messages during agent processing
            const isIntermediaryMessage = isAgentMessage && messageFromFragment?.messageType === "AGENT_PROCESS";

            // Check if there are any tool calls in the thought process (v2) or toolCalls array (v1)
            const hasToolCalls = (messageFromFragment?.agentThoughtProcess?.some(
              (item: any) => item.toolUse != null
            ) ?? false) || (messageFromFragment?.toolCalls && messageFromFragment.toolCalls.length > 0);

            // Show thought process if: in-progress (AGENT_PROCESS) OR completed with tool calls
            const shouldShowThoughtProcess = isAgentMessage && (isIntermediaryMessage || hasToolCalls);

            // Show copy/feedback actions only for final AGENT_RESPONSE messages with metadata
            const shouldShowActions = isAgentMessage && !isIntermediaryMessage && hasMessageMetadata && Boolean(messageContent);
            const shouldCollapseSources = collapseSources;
            // collapseSources wins over the recent-agent auto-expand: the
            // autopilot wizard wants every Sources accordion closed by
            // default, even on the most recent agent message.
            const forceInlineArtifacts =
              !shouldCollapseSources && isAgentMessage && recentAgentIds.has(aiMessage.id);

            // Use stable key based on interaction + role to prevent unmounting during message ID changes
            const interactionId = messageFromFragment?.llmInteractionId || group.interactionId || "no-interaction";
            const stableKey = `${interactionId}-${metadata.messageRole}`;

            return (
              <div
                key={stableKey}
                data-message-id={aiMessage.id}
                className="scroll-mt-4"
              >
                <ChatBubble
                  uiMessage={aiMessage}
                  avatarSrc={metadata.messageRole === "AGENT" ? undefined : userAvatar}
                  avatarFallback={metadata.messageRole === "AGENT" ? "T0" : userInitials}
                  avatarContent={metadata.messageRole === "AGENT" ? <TierZeroAvatar /> : undefined}
                  hideAvatar={hideAvatars}
                  belowMessage={
                    !isAgentMessage && messageFromFragment?.attachments?.length > 0 ? (
                      <MessageAttachmentList attachments={messageFromFragment.attachments} />
                    ) : undefined
                  }
                >
                  <div>
                    {(() => {
                      // Check if this is a v2 message by looking at agentVersion OR presence of agentThoughtProcess field
                      const hasAgentThoughtProcess = messageFromFragment?.agentThoughtProcess != null;
                      const isV2Message = messageFromFragment?.messageMetadata?.agentVersion === "v2" || hasAgentThoughtProcess;

                      return (
                        <>
                          {/* Show agent thought process for in-progress messages or completed messages with tool calls */}
                          {shouldShowThoughtProcess && messageFromFragment && (
                            isV2Message ? (
                              <AgentThoughtProcess
                                message={messageFromFragment}
                                conversation={data}
                                hasTimedOutQuestion={
                                  group.interactionId
                                    ? pendingQuestionsByInteraction.get(group.interactionId)?.some(q => q.status === "TIMEOUT") ?? false
                                    : false
                                }
                                pendingQuestions={
                                  group.interactionId
                                    ? pendingQuestionsByInteraction.get(group.interactionId) ?? []
                                    : []
                                }
                                onQuestionSubmit={(toolUseId, answers) => {
                                  if (group.interactionId) {
                                    handleQuestionSubmit(toolUseId, group.interactionId, answers);
                                  }
                                }}
                                isSubmittingQuestion={isSubmittingQuestion}
                              />
                            ) : (
                              // v1 messages - render tool calls using legacy component
                              messageFromFragment.toolCalls && messageFromFragment.toolCalls.length > 0 ? (
                                <ToolCallsSection
                                  toolCalls={messageFromFragment.toolCalls}
                                  messageMetadata={messageFromFragment.messageMetadata}
                                  messageContent={messageFromFragment.output}
                                  messageType={messageFromFragment.messageType}
                                  isThinking={isIntermediaryMessage}
                                  llmConversationId={llmConversationId}
                                />
                              ) : null
                            )
                          )}

                          {(() => {
                      if (!messageFromFragment) {
                        return (
                          <Markdown>
                            {aiMessage.parts.map((part: any) => (part.type === "text" ? part.text : "")).join("\n")}
                          </Markdown>
                        );
                      }

                      // Only render final output for AGENT_RESPONSE messages
                      // AGENT_PROCESS messages only show thought process (handled above in the accordion)
                      if (isIntermediaryMessage) {
                        return null;
                      }

                      // Render final output with sources and citations
                      return (
                        <>
                          <MessageWithToolReferences
                            message={messageFromFragment}
                            conversationId={llmConversationId}
                            isV2={true}
                            forceInlineArtifacts={forceInlineArtifacts}
                            initiallyCollapsed={shouldCollapseSources}
                            hasTimedOutQuestion={
                              group.interactionId
                                ? pendingQuestionsByInteraction.get(group.interactionId)?.some(q => q.status === "TIMEOUT") ?? false
                                : false
                            }
                            copyButton={
                              shouldShowActions && messageContent ? (
                                <LlmChatMessageActions
                                  llmConversationId={llmConversationId}
                                  messageContent={messageContent}
                                  llmMessageId={messageFromFragment.id}
                                />
                              ) : undefined
                            }
                          />
                          {/* Render UpdateTodoTool if present in toolCalls (v2 only, v1 already renders via ToolCallsSection) */}
                          {isV2Message && messageFromFragment.toolCalls?.map((toolCall: any, idx: number) => {
                            if (toolCall.toolUse?.name === "UpdateTodoTool") {
                              return (
                                <div key={toolCall.toolUse?.id || `todo-${idx}`} className="mt-2">
                                  <ChecklistToolCallResult toolCall={toolCall} />
                                </div>
                              );
                            }
                            return null;
                          })}
                        </>
                      );
                    })()}
                        </>
                      );
                    })()}
                  </div>
                </ChatBubble>
              </div>
            );
          })}
              </div>
            );
          })}

          {/* Show loading bubble only when last message is USER_INPUT and we're waiting for agent response */}
          {isPending && (() => {
            // Always check data.messages (not agentMessages) to see if last overall message is HUMAN
            const relevantMessages = data.messages ?? [];
            if (relevantMessages.length === 0) return false;
            const lastMessage = relevantMessages[relevantMessages.length - 1];
            // Show loading if the last user message has not yet received an agent response
            return lastMessage?.messageRole === "HUMAN";
          })() && (
            <div>
              <AgentLoadingBubble
                timestamp={formatChatTimestamp(new Date().toISOString())}
                statusMessage={activeStatusMessage}
                hideAvatar={hideAvatars}
              />
            </div>
          )}

          <div ref={assignEndRef} />
        </div>
      </ChatMessageList>

      {!isScrolledToBottom ? (
        <button
          type="button"
          onClick={() => {
            if (endOfListRef.current) {
              endOfListRef.current.scrollIntoView({ behavior: "smooth" });
            }
          }}
          className="pointer-events-auto absolute bottom-6 left-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-popover text-foreground transition hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Jump to latest message"
        >
          <ArrowDown className="h-5 w-5" />
        </button>
      ) : null}
    </div>
  );
};

export default LlmConversationMessageList;
